/**
 * Pricing section (V26): the compact intro card and, when opened, "Choose Your Coverage".
 *
 * Every number on screen comes from the one price ladder through the coverage model; nothing is typed here.
 * Presets are 1 / 8 / 12 hours plus Custom. In Custom the presets gray out, a slider walks the twelve coverage hours
 * (one label per hour, the chosen one highlighted) and, where the playbook sells them, a stepper adds photos in tens
 * at $50 a step (Trera, 21 Sep 2026: the bottom numbers legible, photo counts real). Book My Date hands the exact
 * selection to the booking page (package type + hours + photos); the server prices it from the same table.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  SERVICES, DEFAULT_HOURS, variationsFor, variationFor, presetOptions, sliderLabels, photoOptionsFor,
  formatDollars, describeSelection, bookingUrlFor, PHOTO_STEP, PHOTO_STEP_PRICE
} from '@/lib/homepage/coverageModel'

export default function CoverageBuilder({ open, onOpen, onClose }) {
  const navigate = useNavigate()
  const [service, setService] = useState('photo')
  const [hours, setHours] = useState(DEFAULT_HOURS)
  const [photos, setPhotos] = useState(null) // null = the photos included at the base price
  const [customMode, setCustomMode] = useState(false)
  const builderRef = useRef(null)
  const introRef = useRef(null)

  const list = useMemo(() => variationsFor(service), [service])
  const labels = useMemo(() => sliderLabels(service), [service])
  const presets = useMemo(() => presetOptions(service), [service])
  const selection = variationFor(service, hours, photos) || variationFor(service, hours) || list[0] || null
  const sliderIndex = Math.max(0, list.findIndex(v => v.hours === selection?.hours))
  const photoOptions = useMemo(() => photoOptionsFor(service, hours), [service, hours])
  const photoIndex = Math.max(0, photoOptions.findIndex(o => o.photos === (selection?.photos ?? null)))
  const stepPhotos = dir => {
    const next = photoOptions[photoIndex + dir]
    if (next) setPhotos(next.extra === 0 ? null : next.photos)
  }
  const startingAt = variationFor('photo', 1)

  useEffect(() => {
    if (!open || !builderRef.current) return
    const t = setTimeout(() => builderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80)
    return () => clearTimeout(t)
  }, [open])

  const pickService = key => { setService(key); setHours(DEFAULT_HOURS); setPhotos(null); setCustomMode(false) }
  const pickPreset = h => { setCustomMode(false); setHours(h); setPhotos(null) }
  const showCustom = () => setCustomMode(true)
  const onSlide = e => {
    const v = list[Number(e.target.value)]
    if (v && v.hours !== hours) { setHours(v.hours); setPhotos(null) }
  }
  const close = () => {
    onClose?.()
    setTimeout(() => introRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50)
  }
  const book = () => { if (selection) navigate(bookingUrlFor(selection)) }

  return (
    <section className="pricing-section" id="pricing" aria-labelledby="pricing-heading">
      <span id="negotiation-widget" aria-hidden="true" />
      <div className="shell">
        <div className="pricing-intro" ref={introRef}>
          <div>
            <div className="eyebrow">Wedding collections</div>
            <h2 id="pricing-heading" className="serif">Timeless wedding<br />photography starting<br />at {formatDollars(startingAt?.price)}.</h2>
            <p>Explore transparent photo, video, and photo + video collections designed to make booking feel simple, elevated, and stress-free.</p>
            <button type="button" className="btn rose" onClick={() => (open ? close() : onOpen?.())} aria-expanded={open} aria-controls="pricingBuilder">
              VIEW PRICING AND BOOK
            </button>
          </div>
          <div className="price-stack" aria-hidden="true">
            <div className="stack-card one" />
            <div className="stack-card two" />
            <div className="stack-card front">
              <small>Starting at</small>
              <strong>{formatDollars(startingAt?.price)}</strong>
              <span>Photo coverage</span>
              <ul>
                <li>Clear package options</li>
                <li>Photo + video available</li>
                <li>Custom coverage flexibility</li>
              </ul>
            </div>
          </div>
        </div>

        {open && (
          <div className="pricing-builder" id="pricingBuilder" ref={builderRef}>
            <div className="builder-head">
              <div>
                <h2 className="serif">Choose Your Coverage</h2>
                <p>Build your package in seconds.</p>
              </div>
              <div className="builder-actions">
                <div className="service-tabs" role="tablist" aria-label="Package type">
                  {SERVICES.map(s => (
                    <button key={s.key} type="button" role="tab" aria-selected={service === s.key} className={service === s.key ? 'active' : ''} onClick={() => pickService(s.key)}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <button type="button" className="pricing-close" onClick={close} aria-label="Close pricing" title="Close pricing">×</button>
              </div>
            </div>

            <div className="coverage-box">
              <div className="eyebrow">Select coverage</div>
              <p>Choose one of our three pre-selected packages, or select Custom to fine-tune your coverage.</p>
              <div className={`coverage-options${customMode ? ' custom-active' : ''}`} id="coverageOptions">
                {presets.map(p => (
                  <button key={p.id} type="button" className={`coverage-option${!customMode && p.hours === hours ? ' active' : ''}`} onClick={() => pickPreset(p.hours)} aria-pressed={!customMode && p.hours === hours}>
                    <b>{p.hours} Hour{p.hours > 1 ? 's' : ''}</b>
                    <span>{p.tag}</span>
                  </button>
                ))}
                <button type="button" className={`coverage-option custom-choice${customMode ? ' active' : ''}`} onClick={showCustom} aria-pressed={customMode}>
                  <b>Custom</b>
                  <span>Flexible</span>
                </button>
              </div>

              {customMode && selection && (
                <div className="custom-slider" id="customSlider">
                  <p className="slider-help">{photoOptions.length > 1 ? 'Slide to adjust coverage, then add photos — pricing updates automatically.' : 'Slide to adjust coverage — pricing updates automatically.'}</p>
                  <div className="custom-readout">
                    <div>
                      <span>Custom package</span>
                      <b id="sliderHours">{selection.hours} hour{selection.hours > 1 ? 's' : ''}</b>
                      {selection.photos ? <span id="sliderPhotos">{selection.photos} photos</span> : null}
                    </div>
                    <div><b id="sliderPrice">{formatDollars(selection.price)}</b></div>
                  </div>
                  <input
                    type="range" id="hoursSlider" min={0} max={Math.max(0, list.length - 1)} step={1} value={sliderIndex} onChange={onSlide}
                    aria-label="Coverage hours" aria-valuetext={`${selection.hours} hours, ${formatDollars(selection.price)}`}
                  />
                  <div className="slider-track-labels" id="sliderTrackLabels" aria-hidden="true">
                    {labels.map(l => <span key={l.hours} className={`slider-major${l.hours === selection.hours ? ' active' : ''}`} style={{ left: `${l.left}%` }}>{l.hours}h</span>)}
                  </div>
                  {photoOptions.length > 1 && (
                    <div className="photo-stepper" role="group" aria-label="Photo count">
                      <span className="photo-stepper-label">Photos</span>
                      <button type="button" onClick={() => stepPhotos(-1)} disabled={photoIndex <= 0} aria-label="Fewer photos">−</button>
                      <b aria-live="polite">{selection.photos}</b>
                      <button type="button" onClick={() => stepPhotos(1)} disabled={photoIndex >= photoOptions.length - 1} aria-label="More photos">+</button>
                      <small>{photoOptions[0].photos} included · +{PHOTO_STEP} photos adds {formatDollars(PHOTO_STEP_PRICE)} · up to {photoOptions[photoOptions.length - 1].photos}</small>
                    </div>
                  )}
                </div>
              )}

              <div className={`builder-bottom${customMode ? ' custom' : ''}`}>
                {!customMode && selection && (
                  <div>
                    <div className="builder-price" id="builderPrice">{formatDollars(selection.price)}</div>
                    <div className="builder-detail" id="builderDetail">{describeSelection(selection)}</div>
                    <p>Includes editing, gallery, and print rights</p>
                  </div>
                )}
                <button type="button" className="book-big" onClick={book} disabled={!selection}>Book My Date</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
